import { useMemo } from 'react'
import { addDays, format, startOfDay, startOfWeek } from 'date-fns'
import { cn } from '../utils'

/** One day of activity. `date` is an ISO `YYYY-MM-DD` string. */
export interface ContributionDay {
  date: string
  count: number
}

export interface ContributionGraphProps {
  /** Daily counts. Missing days render as level 0 (empty). */
  data: ContributionDay[]
  /**
   * `'calendar-year'` (default): Jan → Dec of `year`, anchored to January.
   * `'trailing-year'`: the 53 weeks ending at `endDate`.
   */
  range?: 'calendar-year' | 'trailing-year'
  /** Calendar year to render when `range='calendar-year'`. Defaults to `endDate`'s year. */
  year?: number
  /** "Today" — days after it render as empty placeholders. Defaults to today. */
  endDate?: Date
  /** 0 = weeks start Sunday, 1 = Monday (default). */
  weekStartsOn?: 0 | 1
  /** Gap between cells, in px. Default 3. */
  gap?: number
  /**
   * Five background colors, level 0 (empty) → level 4 (most active).
   * Defaults to a theme-aware indigo ramp built with `color-mix`.
   */
  colorScale?: readonly [string, string, string, string, string]
  /** Map a day's count + the period max to a 0–4 level. Default: quartiles of max. */
  getLevel?: (count: number, max: number) => 0 | 1 | 2 | 3 | 4
  /** Noun used in the default tooltip + legend, e.g. "messages". Default "contributions". */
  unitLabel?: string
  /** Override the per-cell tooltip text. */
  tooltip?: (day: ContributionDay) => string
  showWeekdayLabels?: boolean
  showMonthLabels?: boolean
  showLegend?: boolean
  className?: string
}

// Theme-aware indigo ramp: empty cells read as muted; active cells mix the
// brand indigo (--chart-1) into the card surface so the gradient works in both
// light and dark mode. Built with color-mix (NOT a `--chart-1`+alpha suffix,
// which renders black — see memory project_chart_palette).
const DEFAULT_SCALE = [
  'var(--muted)',
  'color-mix(in oklab, var(--chart-1) 26%, var(--card))',
  'color-mix(in oklab, var(--chart-1) 50%, var(--card))',
  'color-mix(in oklab, var(--chart-1) 74%, var(--card))',
  'var(--chart-1)',
] as const

function defaultGetLevel(count: number, max: number): 0 | 1 | 2 | 3 | 4 {
  if (count <= 0) return 0
  if (max <= 0) return 1
  const ratio = count / max
  if (ratio <= 0.25) return 1
  if (ratio <= 0.5) return 2
  if (ratio <= 0.75) return 3
  return 4
}

const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000

interface Cell {
  date: Date
  dateStr: string
  count: number
  /** Day from the previous/next year padding the first/last week → blank slot. */
  outOfYear: boolean
  /** In-range day that hasn't happened yet → muted square, no tooltip. */
  future: boolean
}

export function ContributionGraph({
  data,
  range = 'calendar-year',
  year,
  endDate,
  weekStartsOn = 1,
  gap = 3,
  colorScale = DEFAULT_SCALE,
  getLevel = defaultGetLevel,
  unitLabel = 'contributions',
  tooltip,
  showWeekdayLabels = true,
  showMonthLabels = true,
  showLegend = true,
  className,
}: ContributionGraphProps) {
  const { columns, maxCount } = useMemo(() => {
    const countMap = new Map<string, number>()
    for (const d of data) countMap.set(d.date, d.count)

    const today = startOfDay(endDate ?? new Date())

    let firstDay: Date
    let lastDay: Date
    if (range === 'trailing-year') {
      lastDay = today
      firstDay = addDays(today, -52 * 7) // 53 columns ending this week
    } else {
      const y = year ?? today.getFullYear()
      firstDay = startOfDay(new Date(y, 0, 1))
      lastDay = startOfDay(new Date(y, 11, 31))
    }

    const firstMs = firstDay.getTime()
    const lastMs = lastDay.getTime()
    const todayMs = today.getTime()

    const startWeek = startOfWeek(firstDay, { weekStartsOn })
    const endWeek = startOfWeek(lastDay, { weekStartsOn })
    const nWeeks = Math.round((endWeek.getTime() - startWeek.getTime()) / MS_PER_WEEK) + 1

    let maxCount = 0
    const columns: Cell[][] = Array.from({ length: nWeeks }, (_, w) => {
      const weekStart = addDays(startWeek, w * 7)
      return Array.from({ length: 7 }, (_, i) => {
        const date = addDays(weekStart, i)
        const ms = date.getTime()
        const dateStr = format(date, 'yyyy-MM-dd')
        const outOfYear = ms < firstMs || ms > lastMs
        const future = !outOfYear && ms > todayMs
        const count = countMap.get(dateStr) ?? 0
        if (!outOfYear && !future && count > maxCount) maxCount = count
        return { date, dateStr, count, outOfYear, future }
      })
    })

    return { columns, maxCount }
  }, [data, range, year, endDate, weekStartsOn])

  // Month label belongs to the column where a month first appears in-range.
  // Placed by CSS grid column (never computed pixels) so it can't drift/stack.
  const monthLabels = useMemo(() => {
    const labels: { col: number; text: string }[] = []
    let lastMonth = -1
    columns.forEach((week, ci) => {
      const rep = week.find((c) => !c.outOfYear)
      if (!rep) return
      const month = rep.date.getMonth()
      if (month !== lastMonth) {
        labels.push({ col: ci, text: format(rep.date, 'MMM') })
        lastMonth = month
      }
    })
    return labels
  }, [columns])

  const nWeeks = columns.length
  const labelCol = showWeekdayLabels ? 1 : 0
  const weekdayName = (rowIndex: number): string => {
    const wd = (weekStartsOn + rowIndex) % 7 // 0 = Sun … 6 = Sat
    return wd === 1 ? 'Mon' : wd === 3 ? 'Wed' : wd === 5 ? 'Fri' : ''
  }

  function cellTitle(c: Cell): string {
    if (tooltip) return tooltip({ date: c.dateStr, count: c.count })
    const when = format(c.date, 'MMM d, yyyy')
    const n = c.count
    const noun = n === 1 ? unitLabel.replace(/s$/, '') : unitLabel
    return `${n} ${noun} on ${when}`
  }

  const gridStyle = {
    display: 'grid',
    gap: `calc(var(--spacing) * ${gap / 4})`,
    gridTemplateColumns: `${showWeekdayLabels ? 'auto ' : ''}repeat(${nWeeks}, minmax(0, 1fr))`,
    gridTemplateRows: showMonthLabels
      ? `auto repeat(7, minmax(0, 1fr))`
      : `repeat(7, minmax(0, 1fr))`,
  } as const
  const rowOffset = showMonthLabels ? 2 : 1

  return (
    <div className={cn('w-full', className)}>
      <div style={gridStyle}>
        {/* Month labels — each placed in its own grid column. */}
        {showMonthLabels &&
          monthLabels.map(({ col, text }) => (
            <span
              key={`${col}-${text}`}
              className="self-end whitespace-nowrap type-subtext leading-none text-muted-foreground"
              style={{ gridColumn: col + 1 + labelCol, gridRow: 1, justifySelf: 'start' }}
            >
              {text}
            </span>
          ))}

        {/* Weekday labels — fixed first column, aligned to their day row. */}
        {showWeekdayLabels &&
          Array.from({ length: 7 }, (_, i) => {
            const name = weekdayName(i)
            if (!name) return null
            return (
              <span
                key={`wd-${i}`}
                className="flex items-center pr-1 type-subtext leading-none text-muted-foreground"
                style={{ gridColumn: 1, gridRow: i + rowOffset }}
              >
                {name}
              </span>
            )
          })}

        {/* Day cells. */}
        {columns.map((week, wi) =>
          week.map((c, di) => {
            const style = {
              gridColumn: wi + 1 + labelCol,
              gridRow: di + rowOffset,
              aspectRatio: '1 / 1',
            } as const
            if (c.outOfYear) {
              return <div key={`${wi}-${di}`} aria-hidden style={style} />
            }
            // Future days fill out the year's grid as muted, non-interactive squares.
            const bg = c.future ? colorScale[0] : colorScale[getLevel(c.count, maxCount)]
            return (
              <div
                key={`${wi}-${di}`}
                // radius-ignore: ~11px heatmap data cell — 2px is a proportional hairline, below the xs token by design.
                className="w-full rounded-[2px] ring-1 ring-inset ring-border/40"
                style={{ ...style, backgroundColor: bg, opacity: c.future ? 0.5 : undefined }}
                title={c.future ? undefined : cellTitle(c)}
              />
            )
          }),
        )}
      </div>

      {/* Legend */}
      {showLegend && (
        <div className="mt-2 flex items-center justify-end gap-1">
          <span className="mr-0.5 type-subtext leading-none text-muted-foreground">Less</span>
          {colorScale.map((c, i) => (
            <div
              key={i}
              // radius-ignore: 11px heatmap legend swatch — 2px proportional hairline, below the xs token by design.
              className="h-[11px] w-[11px] rounded-[2px] ring-1 ring-inset ring-border/40"
              style={{ backgroundColor: c }}
            />
          ))}
          <span className="ml-0.5 type-subtext leading-none text-muted-foreground">More</span>
        </div>
      )}
    </div>
  )
}
